import type { Logger } from "@expert/logging";
import type {
    GaiaBotMessage,
    GaiaErrorBody,
    GaiaErrorMessage,
    ProactiveMessagePayload,
    ReactiveMessagePayload,
} from "@expert/shared-types";

import {
    handleIncomingMessage,
    logIncomingMessageOnly,
    useTimelineStore,
} from "@asurion-core/expert-workspace-timeline";
import { type TimelineFeatures, useFeatures } from "@expert/features";
import {
    type GaiaWebSocketResponse,
    gaiaWsEventBus,
    isProactiveErrorMessage,
    isProactiveGuidanceMessage,
    isProactiveMessage,
    isProactiveRecommendationMessage,
    isReactiveGuidanceMessage,
    isReactiveMessage,
} from "@expert/gaia";
import { triggerProactiveSound, triggerTabNotification } from "@expert/proactive-notifications";
import { clearDeviceProbe, isDeviceProbe, setDeviceInfo } from "@expert/solve-tools";
import { useEffect, useMemo } from "react";

import { isDeadAirCorrelationId } from "@/dead-air/deadAirCorrelationRegistry";

import { handleGuidedFlowRecommendation } from "../../utils/";

export const useGaiaMessageEvents = (loggerProp: Logger) => {
    const {
        features: {
            isGuidedFlowRecommendationEnabled,
            isProactiveEnabled,
            isProactiveGuidanceEnabled,
            isProactiveGuidanceDarkModeEnabled,
        },
    } = useFeatures<TimelineFeatures>();
    const logger = useMemo(() => loggerProp.child({ module: "useGaiaMessageEvents" }), [loggerProp]);

    useEffect(() => {
        // Reactive Message Handling
        const gaiaEventBusMessageSuccess = gaiaWsEventBus.on(
            "gaia_ws_orchestrator-invoke-success",
            ({ body, correlationId }: GaiaWebSocketResponse) => {
                /*
                    The orchestrator invoke success event returns reactive,
                    proactive, and autocomplete messages. Both proactive and
                    reactive messages are being handled in this event handler.
                    Auto complete messages are being handled in the useGaiaAutoCompleteEvents
                    handler.
                */
                // Dead-air classification asks reuse this path but are consumed out-of-band by
                // requestDeadAirClassification; skip them so their reply never renders as a bubble.
                if (isDeadAirCorrelationId(correlationId)) return;
                if (
                    !isReactiveMessage(body as ReactiveMessagePayload) &&
                    !isReactiveGuidanceMessage(body as ProactiveMessagePayload)
                )
                    return;

                const reactiveBodyForProbe = body as ReactiveMessagePayload;
                if (isDeviceProbe(correlationId)) {
                    clearDeviceProbe(correlationId);
                    setDeviceInfo(
                        {
                            sessionId: reactiveBodyForProbe.sessionGroupId,
                            externalDevices: reactiveBodyForProbe.externalDevices,
                            detectedDevices: reactiveBodyForProbe.devices,
                            origin: "probe",
                        },
                        logger,
                    );
                    return;
                }

                handleIncomingMessage(body as GaiaBotMessage, logger);

                // Clear the expert message's retry now that its reply has arrived. Only the plain reactive
                // reply carries the expert's id (top-level `messageId`); the guidance reply does not, so it
                // is cleared by correlationId in setupOrchestratorTimeout instead.
                const { sessionGroupId, messageId } = body as ReactiveMessagePayload;
                if (sessionGroupId && messageId) {
                    useTimelineStore.getState().updateMessage(sessionGroupId, messageId, {
                        metadata: { retryPayload: undefined },
                    });
                }
            },
        );

        // Proactive Message Handling
        const gaiaEventBusProactiveMessageReceived = gaiaWsEventBus.on(
            "gaia_ws_message-suggestion",
            ({ body }: GaiaWebSocketResponse) => {
                const gaiaSuggestion = body as unknown as ProactiveMessagePayload;

                if (isGuidedFlowRecommendationEnabled && isProactiveRecommendationMessage(gaiaSuggestion)) {
                    handleGuidedFlowRecommendation(gaiaSuggestion, logger);
                    return;
                }

                // All messages are coming through this event (reactive messages + proactive messages)
                const shouldHandleProactiveMessage =
                    (isProactiveGuidanceMessage(gaiaSuggestion) &&
                        (isProactiveGuidanceEnabled || isProactiveGuidanceDarkModeEnabled)) ||
                    (isProactiveMessage(gaiaSuggestion) && isProactiveEnabled);
                if (!shouldHandleProactiveMessage) return;

                // TODO: error messages should not return with messageType = proactive
                // working with gaia team to resolve this
                if (isProactiveErrorMessage(gaiaSuggestion)) {
                    logger.warn(
                        {
                            originalError: gaiaSuggestion.suggestion.message,
                        },
                        "GAIA | websocket proactive error message response",
                    );
                    return;
                }

                if (gaiaSuggestion.suggestion.metadata?.suppressionFlag) {
                    logger.info(
                        {
                            messageId: gaiaSuggestion.suggestion.messageId,
                            suppressionReason: gaiaSuggestion.suggestion.metadata.suppressionReason,
                        },
                        "GAIA | proactive message suppressed",
                    );
                    return;
                }

                if (isProactiveGuidanceMessage(gaiaSuggestion)) {
                    // The visible flag wins when both are on.
                    const isDarkMode = !isProactiveGuidanceEnabled && isProactiveGuidanceDarkModeEnabled;

                    // Happy-path visibility for proactive guidance; the receive path is otherwise silent
                    // on success.
                    logger.info(
                        {
                            sessionId: gaiaSuggestion.sessionGroupId,
                            messageId: gaiaSuggestion.suggestion.messageId,
                            isDarkMode,
                        },
                        "GAIA | proactive guidance received",
                    );

                    // Dark mode: log the received events only. Never show it to the expert (no timeline
                    // message, tab notification, or sound).
                    if (isDarkMode) {
                        void logIncomingMessageOnly(gaiaSuggestion, logger);
                        return;
                    }
                }

                handleIncomingMessage(gaiaSuggestion, logger);
                if (gaiaSuggestion.suggestion.message?.trim()) {
                    triggerTabNotification("Expert Workspace", shouldHandleProactiveMessage);
                    triggerProactiveSound(shouldHandleProactiveMessage);
                }
            },
        );

        const gaiaEventBusOrchestratorFailure = gaiaWsEventBus.on(
            "gaia_ws_orchestrator-invoke-error",
            ({ body, correlationId }: GaiaWebSocketResponse) => {
                // Dead-air asks handle their own errors out-of-band (by correlationId); ignore here.
                if (isDeadAirCorrelationId(correlationId)) return;
                const errorMessage = body as GaiaErrorMessage;

                // Legacy GAIA sends the error body as a stringified JSON under `.body`; the Simplr/
                // logic-builder flow sends a flat object (e.g. `{ message, trackingId }`) with no nested
                // string and no message id. Only parse when it is actually a string, so the new shape no
                // longer trips the catch. Inline retry for an in-flight request is set by correlationId in
                // setupOrchestratorTimeout, which does not depend on the error body carrying an id.
                let errorBody: GaiaErrorBody | undefined;
                if (typeof errorMessage.body === "string") {
                    try {
                        errorBody = JSON.parse(errorMessage.body) as GaiaErrorBody;
                    } catch {
                        logger.error({ errorMessage }, "GAIA | Failed to parse error body");
                    }
                }

                logger.warn(
                    {
                        errorMessage,
                        errorBody,
                    },
                    "GAIA | websocket error message response",
                );
            },
        );

        const gaiaEventBusSessionContextFailure = gaiaWsEventBus.on(
            "gaia_ws_update-session-context-error",
            ({ body }: GaiaWebSocketResponse) => {
                const errorMessage = body as GaiaErrorMessage;

                // Legacy GAIA sends the error body as stringified JSON; the Simplr/logic-builder flow sends
                // a flat object instead. Only parse when it is actually a string so the new shape no longer
                // throws (an uncaught throw here leaks the subscription), matching the orchestrator-failure
                // handler above.
                let errorBody: GaiaErrorBody | undefined;
                if (typeof errorMessage.body === "string") {
                    try {
                        errorBody = JSON.parse(errorMessage.body) as GaiaErrorBody;
                    } catch {
                        logger.error({ errorMessage }, "GAIA | Failed to parse session context error body");
                    }
                }

                logger.warn(
                    {
                        errorMessage,
                        errorBody,
                    },
                    "GAIA | update session context error message response",
                );
            },
        );

        return () => {
            gaiaEventBusMessageSuccess();
            gaiaEventBusOrchestratorFailure();
            gaiaEventBusProactiveMessageReceived();
            gaiaEventBusSessionContextFailure();
        };
    }, [
        isGuidedFlowRecommendationEnabled,
        isProactiveEnabled,
        isProactiveGuidanceEnabled,
        isProactiveGuidanceDarkModeEnabled,
        logger,
    ]);
};
